import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Eraser } from "lucide-react";

const FONTS = ["Helvetica", "Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Tahoma"];
const SIZES = [["Small", "2"], ["Normal", "3"], ["Medium", "4"], ["Large", "5"], ["Huge", "6"]];

export function RichTextEditor({ value, onChange, placeholder = "" }) {
  const ref = useRef(null);
  const saved = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const save = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      saved.current = sel.getRangeAt(0);
    }
  };
  const restore = () => {
    if (saved.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(saved.current);
    }
  };

  const exec = (cmd, arg) => {
    ref.current?.focus();
    restore();
    document.execCommand(cmd, false, arg);
    emit();
    save();
  };

  const btn =
    "h-8 w-8 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-white hover:bg-white/10 transition-colors";

  return (
    <div className="border border-white/15 bg-black/40" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-white/10">
        <select onChange={(e) => { exec("fontName", e.target.value); e.target.selectedIndex = 0; }}
          data-testid="sig-font" defaultValue=""
          className="h-8 bg-black/40 border border-white/15 text-white/70 text-xs px-1">
          <option value="" disabled>Font</option>
          {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>
        <select onChange={(e) => { exec("fontSize", e.target.value); e.target.selectedIndex = 0; }}
          data-testid="sig-size" defaultValue=""
          className="h-8 bg-black/40 border border-white/15 text-white/70 text-xs px-1">
          <option value="" disabled>Size</option>
          {SIZES.map(([l, v]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button type="button" className={btn} data-testid="sig-bold"
          onMouseDown={(e) => { e.preventDefault(); exec("bold"); }}><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} data-testid="sig-italic"
          onMouseDown={(e) => { e.preventDefault(); exec("italic"); }}><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} data-testid="sig-underline"
          onMouseDown={(e) => { e.preventDefault(); exec("underline"); }}><Underline className="h-3.5 w-3.5" /></button>
        <label className={`${btn} cursor-pointer relative`} data-testid="sig-color-wrap" title="Text color">
          <span className="text-[13px] font-semibold leading-none">A</span>
          <input type="color" data-testid="sig-color"
            onChange={(e) => exec("foreColor", e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer" />
        </label>
        <button type="button" className={btn} data-testid="sig-ul"
          onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }}><List className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} data-testid="sig-ol"
          onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }}><ListOrdered className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} data-testid="sig-clear"
          onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }} title="Clear formatting"><Eraser className="h-3.5 w-3.5" /></button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning dir="ltr"
        data-testid="signature-input"
        onInput={emit} onKeyUp={save} onMouseUp={save} onBlur={save}
        data-placeholder={placeholder}
        className="rte-surface min-h-[170px] p-3 text-sm text-white/85 outline-none leading-relaxed"
        style={{ fontFamily: "Helvetica, Arial, sans-serif" }} />
    </div>
  );
}
